import {
  AddCircleOutlineRounded,
  RemoveCircleOutlineRounded,
} from "@mui/icons-material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Alert,
  AlertTitle,
  Box,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import useAPI from "../../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../../api/hooks/useColors";
import CustomToolTip from "../../../../../components/CustomTooltip";
import CustomToolTipWithPermission from "../../../../../components/CustomToolTipWithPermission";
import InfoCalculationButton from "../../../../../components/InfoCalculationButton";
import {
  ComplexEntityTypes,
  PLATFORM_USER_ACTIONS,
} from "../../../../../utils/constants";
import ChipColor from "../../../../remediation/plan/components/ChipColor";
import GapsTabRisk from "./GapsTabRisk";
import VulnerabilitiesTabRisk from "./VulnerabilitiesTabRisk";

function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`security-issues-tabpanel-${index}`}
      aria-labelledby={`security-issues-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const RiskSummarySection = ({
  entityData,
  onExcludeClick,
  setRiskAnchorEl,
  riskAnchorEl,
}) => {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.EXCLUDE_OR_INCLUDE_ENTITY_FROM_RISK
  );

  const [activeTab, setActiveTab] = useState(
    entityData?.related_control_ids?.length > 0 ? 0 : 1
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    trackEvent(
      `Switch to ${newValue === 0 ? "Gaps" : "Vulnerabilities"} tab for ${
        entityData?.entity_type
      } ${entityData?.unique_id}`
    );
  };

  const handleViewAllGaps = () => {
    if (entityData?.related_control_ids?.length > 0) {
      const params = new URLSearchParams(location.search);
      params.set("gaps", encodeURIComponent(entityData.entity_id));
      setSearchParams(params.toString());
      trackEvent(
        `Open Gaps Drawer for ${entityData?.entity_type} ${entityData?.unique_id}`
      );
    }
  };

  const handleViewAllVulnerabilities = () => {
    if (entityData?.vulnerability_count > 0) {
      const params = new URLSearchParams(location.search);
      params.set("vulnerabilities", encodeURIComponent(entityData.entity_id));
      setSearchParams(params.toString());
      trackEvent(
        `Open Vulnerabilities Drawer for ${entityData?.entity_type} ${entityData?.unique_id}`
      );
    }
  };

  const handleCellClick = (params) => {
    window.open(
      `/posture/controls?control_id=${params.row.control_id}`,
      "_blank"
    );
  };

  const handleVulnerabilityRowClick = (row) => {
    window.open(
      `/posture/vulnerabilities?cve=${row.row.vulnerability_id}`,
      "_blank"
    );
    trackEvent("User open Vulnerability Drawer");
  };

  const renderViewAllGaps = () => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            cursor: "pointer",
            color: colors.text_secondary,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
          onClick={handleViewAllGaps}
        >
          View all gaps
        </Typography>
        <ArrowForwardIcon
          sx={{
            width: "12px",
          }}
        />
      </Box>
    );
  };
  const renderViewAllVulnerabilities = () => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            cursor: "pointer",
            color: colors.text_secondary,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
          onClick={handleViewAllVulnerabilities}
        >
          View all vulnerabilities
        </Typography>
        <ArrowForwardIcon
          sx={{
            width: "12px",
          }}
        />
      </Box>
    );
  };

  const renderRiskExclusionControl = () => {
    if (!entityData?.risk) return null;

    return (
      <CustomToolTipWithPermission
        title={null}
        permission={hasPermission}
        placement="top"
      >
        <span>
          <Typography
            variant="caption"
            sx={{
              cursor: "pointer",
              color: colors.text_secondary,
              display: "flex",
              alignItems: "center",
              gap: 1,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={onExcludeClick}
          >
            {(entityData?.is_excluded_from_all
              ? "Include in "
              : "Exclude from ") + "Risk"}
            {entityData?.is_excluded_from_all ? (
              <AddCircleOutlineRounded
                sx={{
                  width: "16px",
                  height: "16px",
                  color: colors.grey_700,
                }}
              />
            ) : (
              <RemoveCircleOutlineRounded
                sx={{
                  width: "16px",
                  height: "16px",
                  color: colors.grey_700,
                }}
              />
            )}
          </Typography>
        </span>
      </CustomToolTipWithPermission>
    );
  };

  const gapTab = () => {
    return (
      <Tab
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2">Gaps</Typography>
          </Box>
        }
        id="security-issues-tab-0"
        disabled={!entityData?.related_control_ids?.length}
      />
    );
  };

  const vulnerabilityTab = () => {
    return (
      <Tab
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2">Vulnerabilities</Typography>
          </Box>
        }
        id="security-issues-tab-1"
        disabled={!entityData?.vulnerability_count}
      />
    );
  };

  const hasSecurityIssues =
    entityData?.related_control_ids?.length > 0 ||
    entityData?.vulnerability_count;

  return (
    <Grid item xs={12} display="flex" flexDirection="column" gap={2}>
      <Box display="flex" flexDirection="column" alignItems="flex-start">
        <Typography variant="subtitle1">Risk Summary</Typography>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          {entityData?.risk && (
            <ChipColor
              value={entityData?.risk}
              type="entity_risk"
              style={{
                width: "100px",
                height: "25px",
              }}
            />
          )}
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap={2}
            mt={"12px"}
          >
            <InfoCalculationButton
              label="How is risk calculated?"
              anchorEl={riskAnchorEl}
              setAnchorEl={setRiskAnchorEl}
            />

            {activeTab === 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                {renderRiskExclusionControl()}
                {renderViewAllGaps()}
              </Box>
            )}
            {activeTab === 1 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                {renderViewAllVulnerabilities()}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {hasSecurityIssues ? (
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          sx={{
            boxShadow: "0px 0px 2px 1px rgba(203, 203, 203, 0.50)",
            background: "white",
            borderRadius: "10px",
          }}
        >
          <Box>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="security issues tabs"
              sx={{
                minHeight: "36px",
                "& .MuiTab-root": {
                  textTransform: "none",
                  minHeight: "36px",
                  py: 0.5,
                  fontWeight: 500,
                  color: colors.text_secondary,
                },
                "& .Mui-selected": {
                  color: colors.text_primary,
                  fontWeight: 700,
                  background: "#FFF",
                  borderRadius: "8px 8px 0 0",
                  boxShadow: "0px -1px 4px 2px rgba(0, 0, 0, 0.1)",
                },
                textTransform: "none",
                "& .MuiTabs-indicator": {
                  backgroundColor: "transparent",
                  height: 3,
                },
              }}
            >
              {!entityData?.related_control_ids?.length ? (
                <CustomToolTip
                  title={
                    !entityData?.related_control_ids?.length
                      ? "No gaps affecting this entity"
                      : ""
                  }
                  placement="top"
                >
                  <Box>{gapTab()}</Box>
                </CustomToolTip>
              ) : (
                gapTab()
              )}

              {(entityData?.entity_type === ComplexEntityTypes.SERVER ||
                entityData?.entity_type === ComplexEntityTypes.HOST ||
                entityData?.entity_type === ComplexEntityTypes.DATA_STORE) &&
                (!entityData?.vulnerability_count ? (
                  <CustomToolTip
                    title={
                      !entityData?.vulnerability_count
                        ? "No vulnerabilities affecting this entity"
                        : ""
                    }
                    placement="top"
                  >
                    <Box>{vulnerabilityTab()}</Box>
                  </CustomToolTip>
                ) : (
                  vulnerabilityTab()
                ))}
            </Tabs>
          </Box>

          <TabPanel value={activeTab} index={0}>
            <GapsTabRisk
              entityData={entityData}
              handleCellClick={handleCellClick}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <VulnerabilitiesTabRisk
              entityData={entityData}
              onRowClick={handleVulnerabilityRowClick}
              pageSizeOptionsOverride={[3]}
            />
          </TabPanel>
        </Box>
      ) : (
        <Alert severity="success" sx={{ m: 2 }}>
          <AlertTitle>You're all set!</AlertTitle>
          No gaps affect this entity.
        </Alert>
      )}
    </Grid>
  );
};

export default RiskSummarySection;
